import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "checkbox", "submitButton", "validationMessage", "messagePreview", "reason", "reasonValidation"]
  static values = { escalateUrl: String }

  messageId = null

  connect() {
    document.addEventListener('keydown', this.handleKeydown.bind(this))
  }

  disconnect() {
    document.removeEventListener('keydown', this.handleKeydown.bind(this))
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.hasModalTarget && !this.modalTarget.classList.contains('hidden')) {
      this.close()
    }
  }

  open(event) {
    if (!this.hasModalTarget) return

    this.messageId = event.currentTarget.dataset.messageId

    // Get message content from DOM (more reliable than data attribute for streamed messages)
    let messageContent = this.getMessageContentFromDOM(this.messageId)
    // Fallback to data attribute if DOM content not found
    if (!messageContent) {
      messageContent = event.currentTarget.dataset.messageContent
    }

    if (this.hasMessagePreviewTarget) {
      this.messagePreviewTarget.textContent = messageContent || ''
    }

    // Reset checkboxes
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = false
    })

    // Reset reason textarea
    if (this.hasReasonTarget) {
      this.reasonTarget.value = ''
    }

    // Reset validation
    this.validateSelection()

    // Show modal
    this.modalTarget.classList.remove('hidden')
    document.body.style.overflow = 'hidden'
  }

  getMessageContentFromDOM(messageId) {
    const contentElement = document.getElementById(`message_${messageId}_content`)
    if (contentElement) {
      const text = contentElement.textContent?.trim() || ''
      return text.length > 150 ? text.substring(0, 150) + '...' : text
    }
    return null
  }

  close() {
    if (!this.hasModalTarget) return

    this.modalTarget.classList.add('hidden')
    document.body.style.overflow = ''
    this.messageId = null
  }

  validateSelection() {
    const anyChecked = this.checkboxTargets.some(checkbox => checkbox.checked)
    const reason = this.hasReasonTarget ? this.reasonTarget.value.trim() : ''
    const hasReason = reason.length > 0
    const isValid = anyChecked && hasReason

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !isValid
    }

    if (this.hasValidationMessageTarget) {
      this.validationMessageTarget.classList.toggle('hidden', anyChecked)
    }

    if (this.hasReasonValidationTarget) {
      this.reasonValidationTarget.classList.toggle('hidden', hasReason)
    }

    return isValid
  }

  async submit() {
    if (!this.validateSelection()) {
      return
    }

    const participants = this.checkboxTargets
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value)

    if (participants.length === 0) {
      return
    }

    // Disable submit button
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.textContent = 'Starting...'
    }

    const reason = this.hasReasonTarget ? this.reasonTarget.value.trim() : ''

    try {
      const response = await fetch(this.escalateUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          message_id: this.messageId,
          participants: participants,
          reason: reason
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this.close()
        // Redirect to the new chat
        window.location.href = data.redirect_url
      } else {
        alert(data.error || 'Failed to escalate message. Please try again.')
        // Re-enable submit button
        if (this.hasSubmitButtonTarget) {
          this.submitButtonTarget.disabled = false
          this.submitButtonTarget.textContent = 'Start Conversation'
        }
      }
    } catch (error) {
      console.error('Escalation error:', error)
      alert('Failed to escalate message. Please try again.')
      // Re-enable submit button
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false
        this.submitButtonTarget.textContent = 'Start Conversation'
      }
    }
  }
}
